import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import Config, { PaginationLimit } from "../../Config";
import TenantMiniProfile from "../Common/TenantMiniProfile";
import Tooltip from "../Tooltip";
import { MdOutlineChevronRight } from "react-icons/md";
import { truncateText } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";

const ParticipantsTable = ({
    apiData,
    setSelectedData,
    onMealToggle,
    isUpdatingMeal
}) => {
    const navigate = useNavigate();
    const [data, setData] = useState([
        {
            deviceName: "",
            extractionTime: "",
            processingTime: "",
            drops: "",
            action: "",
        },
    ]);

    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    // Function to get status badge with appropriate styling
    const getStatusBadge = (status) => {
        const statusMap = {
            [Config.MEAL_CONSUMPTION_STATUS.PENDING]: {
                label: 'Pending',
                className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
            },
            [Config.MEAL_CONSUMPTION_STATUS.CONSUMED]: {
                label: 'Consumed',
                className: 'bg-green-100 text-green-800 border-green-200'
            },
            [Config.MEAL_CONSUMPTION_STATUS.SKIPPED]: {
                label: 'Skipped',
                className: 'bg-red-100 text-red-800 border-red-200'
            }
        };

        const statusInfo = statusMap[status] || {
            label: 'Unknown',
            className: 'bg-gray-100 text-gray-800 border-gray-200'
        };

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${statusInfo.className}`}>
                {/* <span className="w-1.5 h-1.5 rounded-full mr-1.5 inline-block"
                    style={{
                        backgroundColor: status === Config.MEAL_CONSUMPTION_STATUS.CONSUMED ? '#22c55e' :
                            status === Config.MEAL_CONSUMPTION_STATUS.PENDING ? '#eab308' :
                                status === Config.MEAL_CONSUMPTION_STATUS.SKIPPED ? '#ef4444' : '#9ca3af'
                    }} /> */}
                {statusInfo.label}
            </span>
        );
    };

    // Function to render status with toggle button for pending
    const renderStatusWithToggle = (item) => {
        const status = item.status;
        const isPending = status === Config.MEAL_CONSUMPTION_STATUS.PENDING;
        const isUpdating = isUpdatingMeal;

        if (isPending) {
            return (
                <div className="flex items-center gap-3">
                    {/* <span className="text-xs text-gray-500 font-medium">
                        Pending
                    </span> */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMealToggle(item.id, status);
                        }}
                        disabled={isUpdating}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 ${isUpdating ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                    >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 translate-x-1 ${isUpdating ? 'opacity-50' : ''
                            }`} />
                    </button>
                    {isUpdating && (
                        <FaSpinner className="animate-spin text-gray-400 ml-1" size={14} />
                    )}
                </div>
            );
        }

        return getStatusBadge(status);
    };

    const handleNavigateToTenantService = (tenantId) => {
        if (tenantId) {
            navigate(`/tenant-service?tenantId=${tenantId}`);
        }
    };
    const ActionBtns = (item) => {
        return (
            <div className="flex items-center gap-3">
                <Tooltip label="Click to view">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleNavigateToTenantService(item.tenantId);
                        }}
                        className="text-gray-400 hover:text-blue-600 transition"
                    >
                        <MdOutlineChevronRight size={22} />
                    </button>
                </Tooltip>
            </div>
        );
    };

    const getDescription = (description) => {
        return (
            <div className="flex items-center gap-3">
                <Tooltip label={description}>
                    <span
                        className="text-gray-600"
                    >
                        {truncateText(description, 35)}{" "}
                    </span>
                </Tooltip>
            </div>
        );
    };

    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                name: getName(item),
                getPropName: getPropName(item),
                // status: getStatusBadge(item.status),
                status: renderStatusWithToggle(item),
                mealDescription: getDescription(item.mealDescription),
                action: ActionBtns(item),
                originalItem: item
            }));
            setData(tempData);
        }
    }, [apiData, openActionMenu]);

    const getName = (item) => {
        return <TenantMiniProfile
            tenantId={item.tenantId}
            name={item.tenantName}
            mobile={item.tenantMobile}
            profilePicture={item.profilePicture}
            kycStatus={item.kycStatus}
            flatName={
                <div className="text-xs leading-5">
                    <div>Room: {item?.roomNum}</div>
                    {item?.flatName &&
                        <div>Flat: {item?.flatFloorName}</div>
                    }
                    <div>Property: {item?.propName}</div>
                </div>
            }
        />
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getPropName = (item) => {
        return (
            <div className="flex flex-col">
                <span className="text-gray-600 text-sm block max-w-[160px] break-words line-clamp-2">{item.roomNum}, {item.flatFloorName}, {item.propName} </span>
            </div>
        );
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Tenant",
                    accessor: "name",
                },
                {
                    Header: "Property",
                    accessor: "getPropName",
                },
                {
                    Header: "Status",
                    accessor: "status",
                },
            ];
        }
        return [
            {
                Header: "Tenant",
                accessor: "name",
            },
            {
                Header: "Property",
                accessor: "getPropName",
            },
            {
                Header: "Meal",
                accessor: "mealDescription",
            },
            {
                Header: "Status",
                accessor: "status",
            },
            // {
            //     Header: "",
            //     accessor: "action",
            // },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        setCurrentPage(pageIndex);
    }, [pageIndex]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(apiData?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);

                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr {...row.getRowProps({ style: { height: "80px" } })} hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {typeof cell.value === "function"
                                                    ? cell.value()  // render dynamic function
                                                    : cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Name:</strong> {data[rowIndex]?.originalItem?.tenantName || 'N/A'}
                                                </li>
                                                <li>
                                                    <strong>Room:</strong> {data[rowIndex]?.originalItem?.roomNum || 'N/A'}
                                                </li>
                                                <li>
                                                    <strong>Status:</strong> {data[rowIndex]?.status}
                                                </li>
                                                {/* <li>
                                                    <strong>Action:</strong> {data[rowIndex]?.action}
                                                </li> */}
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table` w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`
  border-b last:border-b-0
  hover:bg-slate-50
  transition-colors
`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;

export default ParticipantsTable;